import { useState, useEffect } from 'react';
import { ArrowRightLeft, Search, WholeWordIcon } from 'lucide-react';
import { translateText } from '../hooks/useTranslation';
import WordCard from '../components/WordCard';
import { searchWord, getSuggestions, DictionaryResponse } from '../services/dictionary';
import SearchBar from '../components/SearchBar';
import { div } from 'framer-motion/client';

export default function Translate() {
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [word, setWord] = useState(undefined);
  const [wordData, setWordData] = useState<DictionaryResponse | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [translatedWord, setTranslatedWord] = useState<string | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState('pt');
  const [targetLanguage, setTargetLanguage] = useState('en');

  const translate = async (text: string) => {
    const translationResult = await translateText(text, sourceLanguage, targetLanguage);
    setTranslatedWord(translationResult);
  }

  const handleSearch = async (word: string) => {
    setIsSearching(true);
    setError(null);
    try {
      const data = await searchWord(word);
      setWordData(data);
      translate(word)
    } catch (err) {
      setTranslatedWord(undefined)
      setError('Word not found. Please try another word.');
      console.error('Error searching word:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = async (word: string) => {
    if (word.length > 1) {
      const newSuggestions = await getSuggestions(word);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (word: string) => {
    handleSearch(word);
  };

  const switchSourceLanguage = () => {
    const tempSourceLanguage = sourceLanguage;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(tempSourceLanguage);
    const tempWord = word;
    setWord(translatedWord || undefined);
    setTranslatedWord(tempWord);
  }

  useEffect(() => {
    if (word) {
      translate(word)
    } else {
      setTranslatedWord(undefined)
    }
  }, [word])

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <header className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <WholeWordIcon className="w-12 h-12 text-purple-600" />
                <h1 className="text-3xl text-left md:text-4xl font-bold text-gray-900">Translate</h1>
              </div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Search Any Word and Learn!
              </p>
          </header>
          <div className='flex flex-col'>
            <div className='flex flex-row items-center gap-4 pb-4 justify-center'>
              <p className='w-[7rem]'>{sourceLanguage === 'pt' ? 'Portuguese 🇧🇷' : 'English 🇺🇸'}</p>
              <ArrowRightLeft className='w-8 h-8 text-gray-600 cursor-pointer text-2xl hover:bg-gray-200 hover:shadow-2xl rounded-full' onClick={switchSourceLanguage} />
              <p className='w-[7rem]'>{targetLanguage === 'pt' ? 'Portuguese 🇧🇷' : 'English 🇺🇸'}</p>
            </div>
            <div className='flex flex-col justify-between gap-4 items-center w-full'>
                  <div className='flex flex-row w-full md:w-[50%] relative'>
                    <input 
                      type='text'
                      placeholder={`Type the words in ${sourceLanguage === 'pt' ? 'Portuguese' : 'English'}`}
                      className='flex-1 py-3 px-4 pl-12 rounded-lg border border-gray-300 focus:outline-none focus:ring focus:ring-blue-300'
                      value={word}
                      onChange={(e) => setWord(e.target.value)}
                    />
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
              <div className='w-full md:w-[50%]'>
                <WordCard word={translatedWord} progress={undefined} disableFlip={true} sourceLanguage={sourceLanguage} />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className={`p-4 rounded-lg mb-6 ${
            error.includes('successfully') 
              ? 'bg-green-100 text-green-700' 
              : 'bg-red-100 text-red-700'
          }`}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}